import { useState } from "react";
import { LogOut, LayoutGrid, Inbox, FileText, User2, MessageSquare, Images, Layers, Phone, KeyRound, ExternalLink, Users, Type } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import EstimatesPanel from "@/pages/admin/panels/EstimatesPanel";
import HeroPanel from "@/pages/admin/panels/HeroPanel";
import AboutPanel from "@/pages/admin/panels/AboutPanel";
import ContactPanel from "@/pages/admin/panels/ContactPanel";
import ServicesPanel from "@/pages/admin/panels/ServicesPanel";
import TestimonialsPanel from "@/pages/admin/panels/TestimonialsPanel";
import GalleryPanel from "@/pages/admin/panels/GalleryPanel";
import SectionsPanel from "@/pages/admin/panels/SectionsPanel";
import AccountPanel from "@/pages/admin/panels/AccountPanel";
import UsersPanel from "@/pages/admin/panels/UsersPanel";

const TABS = [
  { key: "estimates", label: "Estimate Leads", icon: Inbox, component: EstimatesPanel },
  { key: "hero", label: "Hero Section", icon: LayoutGrid, component: HeroPanel },
  { key: "about", label: "About Hondo", icon: FileText, component: AboutPanel },
  { key: "services", label: "Services", icon: Layers, component: ServicesPanel },
  { key: "testimonials", label: "Testimonials", icon: MessageSquare, component: TestimonialsPanel },
  { key: "gallery", label: "Gallery", icon: Images, component: GalleryPanel },
  { key: "sections", label: "Section Copy", icon: Type, component: SectionsPanel },
  { key: "contact", label: "Contact Info", icon: Phone, component: ContactPanel },
  { key: "users", label: "Admin Users", icon: Users, component: UsersPanel },
  { key: "account", label: "My Account", icon: KeyRound, component: AccountPanel },
];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const { refresh } = useContent();
  const [active, setActive] = useState("estimates");

  const ActivePanel = TABS.find((t) => t.key === active)?.component || (() => null);

  return (
    <div className="min-h-screen bg-[#121212] text-white flex flex-col md:flex-row" data-testid="admin-dashboard">
      <aside className="md:w-72 md:min-h-screen bg-[#0d0d0d] border-b md:border-b-0 md:border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF6B00] flex items-center justify-center">
              <span className="font-display text-xl leading-none">LC</span>
            </div>
            <div>
              <div className="font-display uppercase text-lg tracking-wider leading-none">LC Builders</div>
              <div className="text-[10px] tracking-[0.3em] text-white/50 mt-1">ADMIN PANEL</div>
            </div>
          </div>
          <div className="mt-4 text-xs text-white/50 truncate" data-testid="admin-user-email">{admin?.email}</div>
        </div>

        <nav className="flex-1 p-3 grid grid-cols-2 md:grid-cols-1 gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={`flex items-center gap-3 px-4 py-3 text-left border-l-2 transition-colors ${
                active === t.key
                  ? "bg-[#1A1D21] border-[#FF6B00] text-white"
                  : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <t.icon size={16} />
              <span className="text-sm font-medium">{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-[#FF6B00] transition-colors"
          >
            <ExternalLink size={16} /> View Live Site
          </a>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut size={16} /> Log Out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 max-w-5xl">
        <ActivePanel onSaved={refresh} />
      </main>
    </div>
  );
}
